import type { SetTenantSmtpConnectionInput } from "~/gql/graphqlBase.generated";
import type { PendingAddress, SenderLine } from "~/helpers/aiChat/fernieSenderSetup";

import { Box, Button, TextField } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CancelButton, SubmitButton } from "~/components/Buttons";
import FormModal from "~/components/FormModal";
import LINE_SX from "~/components/fernie/fernieStyles";
import RetryableErrorState from "~/components/RetryableErrorState";
import { CaptionText } from "~/components/Typographies";
import fernieSenderSetup from "~/helpers/aiChat/fernieSenderSetup";
import useFernieSenderSetup from "~/hooks/useFernieSenderSetup";
import DomainDnsRecordsTable from "~admin/email/components/DomainDnsRecordsTable";
import SmtpSetupModal from "~admin/email/components/SmtpSetupModal";
import useEmailConnectionMutations from "~admin/email/hooks/useEmailConnectionMutations";
import useEmailSenderMutations from "~admin/email/hooks/useEmailSenderMutations";

interface FernieSenderSetupProps {
	isActive: boolean;
}

const BUTTON_SX = { alignSelf: "flex-start", fontSize: "0.7rem", px: 0 } as const;

const HINT_SX = { ...LINE_SX, color: "text.secondary" } as const;

const FernieSenderSetup = ({ isActive }: FernieSenderSetupProps) => {
	const { t } = useTranslation("common");
	const { isError, refetch, smtp, view } = useFernieSenderSetup(isActive);
	const { completeSenderVerification, isVerifying } = useEmailSenderMutations();
	const { isSaving, saveConnection } = useEmailConnectionMutations();
	const [linkAddress, setLinkAddress] = useState<PendingAddress | null>(null);
	const [link, setLink] = useState("");
	const [isSmtpOpen, setIsSmtpOpen] = useState(false);
	const [smtpOverVerified, setSmtpOverVerified] = useState<SetTenantSmtpConnectionInput | null>(null);

	if (!view && !isError) return null;

	const translateSenderLine = ({ key, values }: SenderLine) => t(key, values);

	const closeLink = () => {
		setLinkAddress(null);
		setLink("");
	};

	const submitLink = async () => {
		if (isVerifying || !linkAddress || !link.trim()) return;
		if (await completeSenderVerification(linkAddress.senderId, link.trim())) closeLink();
	};

	const submitSmtp = async (input: SetTenantSmtpConnectionInput) => {
		if (!view?.smtpIsVerified) return saveConnection(input);
		setSmtpOverVerified(input);
		return false;
	};

	const saveOverVerified = async () => {
		if (!smtpOverVerified) return;
		const ok = await saveConnection(smtpOverVerified);
		setSmtpOverVerified(null);
		if (ok) setIsSmtpOpen(false);
	};

	return (
		<Box
			aria-label={t("fernieChat.senderSetup.title")}
			component="section"
			sx={{
				border: "1px solid",
				borderColor: "divider",
				borderRadius: "6px",
				display: "flex",
				flexDirection: "column",
				gap: 0.75,
				mb: 2,
				px: 1,
				py: 0.75
			}}
		>
			<CaptionText bold sx={LINE_SX}>
				{t("fernieChat.senderSetup.title")}
			</CaptionText>
			{isError && (
				<RetryableErrorState dense message={t("fernieChat.senderSetup.loadError")} onRetry={refetch} title="" />
			)}
			{view && (
				<>
					<CaptionText sx={LINE_SX}>{t(view.statusKey)}</CaptionText>
					{view.methods.map(({ method, state }) => (
						<CaptionText key={method} sx={LINE_SX}>
							{t("fernieChat.senderSetup.methodLine", {
								method: t(`fernieChat.senderSetup.method.${method}`),
								state: t(fernieSenderSetup.getStateLabelKey(state))
							})}
						</CaptionText>
					))}
					{view.routeLines.map((routeLine) => (
						<CaptionText key={routeLine.key} sx={HINT_SX}>
							{translateSenderLine(routeLine)}
						</CaptionText>
					))}
					{view.temporaryOffer && (
						<Box
							aria-label={t("fernieChat.senderSetup.temporary.title")}
							role="group"
							sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
						>
							<CaptionText bold sx={LINE_SX}>
								{t("fernieChat.senderSetup.temporary.title")}
							</CaptionText>
							<CaptionText sx={LINE_SX}>{translateSenderLine(view.temporaryOffer.identity)}</CaptionText>
							{view.temporaryOffer.reply && (
								<CaptionText sx={LINE_SX}>{translateSenderLine(view.temporaryOffer.reply)}</CaptionText>
							)}
							<CaptionText sx={HINT_SX}>{t("fernieChat.senderSetup.temporary.effects")}</CaptionText>
							<CaptionText sx={HINT_SX}>{t("fernieChat.senderSetup.temporary.howToChoose")}</CaptionText>
						</Box>
					)}
					{view.dnsRecords.length > 0 && (
						<>
							<CaptionText sx={HINT_SX}>{t("fernieChat.senderSetup.domain.recordsHint")}</CaptionText>
							<DomainDnsRecordsTable records={view.dnsRecords} />
						</>
					)}
					{view.pendingAddresses.map((address) => (
						<Button key={address.senderId} onClick={() => setLinkAddress(address)} size="small" sx={BUTTON_SX}>
							{t("fernieChat.senderSetup.address.pasteLinkFor", { email: address.email })}
						</Button>
					))}
					<CaptionText sx={HINT_SX}>{t("fernieChat.senderSetup.smtpDisclosure")}</CaptionText>
					<Button onClick={() => setIsSmtpOpen(true)} size="small" sx={BUTTON_SX}>
						{t(smtp ? "fernieChat.senderSetup.smtp.edit" : "fernieChat.senderSetup.smtp.setUp")}
					</Button>
				</>
			)}
			{linkAddress && (
				<FormModal
					actions={
						<>
							<CancelButton onClick={closeLink} />
							<SubmitButton disabled={isVerifying || !link.trim()} onClick={submitLink}>
								{t(isVerifying ? "fernieChat.senderSetup.address.verifying" : "fernieChat.senderSetup.address.verify")}
							</SubmitButton>
						</>
					}
					maxWidth="480px"
					onClose={closeLink}
					onSubmit={submitLink}
					open
					title={t("fernieChat.senderSetup.address.linkTitle")}
				>
					<TextField
						autoComplete="off"
						fullWidth
						helperText={t("fernieChat.senderSetup.address.linkHelp", { email: linkAddress.email })}
						label={t("fernieChat.senderSetup.address.linkLabel")}
						onChange={(event) => setLink(event.target.value)}
						value={link}
					/>
				</FormModal>
			)}
			{isSmtpOpen && (
				<SmtpSetupModal
					initialConnection={smtp}
					onClose={() => setIsSmtpOpen(false)}
					onDirtyChange={() => {}}
					onSubmit={submitSmtp}
					open
					saving={isSaving}
				/>
			)}
			{smtpOverVerified && (
				<FormModal
					actions={
						<>
							<CancelButton onClick={() => setSmtpOverVerified(null)} />
							<SubmitButton disabled={isSaving} onClick={saveOverVerified}>
								{t("fernieChat.senderSetup.smtp.replaceConfirm")}
							</SubmitButton>
						</>
					}
					maxWidth="420px"
					onClose={() => setSmtpOverVerified(null)}
					open
					title={t("fernieChat.senderSetup.smtp.replaceTitle")}
				>
					<CaptionText sx={LINE_SX}>{t("fernieChat.senderSetup.smtp.replaceBody")}</CaptionText>
				</FormModal>
			)}
		</Box>
	);
};

export default FernieSenderSetup;
